'use client'

import { useEffect, useRef } from 'react'
import { useTheme } from 'next-themes'
import { LinkButton } from './ui/Button'
import { Badge } from './ui/Badge'

export default function Hero() {
  const particlesRef = useRef<HTMLDivElement>(null)
  const { resolvedTheme } = useTheme()

  useEffect(() => {
    const container = particlesRef.current
    if (!container) return

    const isDark = resolvedTheme === 'dark'
    const colors = isDark
      ? ['#4B9CD3', '#F0C1CC', '#E8C547', '#FAF8F5']
      : ['#1A5FA8', '#C03060', '#A07800', '#1E4A7A']
    const peakOpacity = isDark ? '.8' : '1'
    const animName = isDark ? 'float-up-dark' : 'float-up-light'
    const kfId = `particle-kf-${isDark ? 'dark' : 'light'}`

    const vw = window.innerWidth
    const count = vw < 640 ? 10 : vw < 1024 ? 15 : 20
    const sizeBase = vw < 640 ? 1.5 : vw < 1024 ? 2 : 2
    const sizeRange = isDark
      ? (vw < 640 ? 2.5 : vw < 1024 ? 3 : 4)
      : (vw < 640 ? 3.5 : vw < 1024 ? 5 : 6)

    const particles: HTMLDivElement[] = []

    for (let i = 0; i < count; i++) {
      const p = document.createElement('div')
      const size = Math.random() * sizeRange + sizeBase
      const color = colors[Math.floor(Math.random() * colors.length)]
      const delay = Math.random() * 8
      const duration = Math.random() * 10 + 8
      const left = Math.random() * 100

      p.style.cssText = `
        position:absolute;border-radius:50%;
        width:${size}px;height:${size}px;
        background:${color};left:${left}%;
        animation:${animName} ${duration}s ${delay}s linear infinite;
        opacity:0;
      `
      container.appendChild(p)
      particles.push(p)
    }

    if (!document.getElementById(kfId)) {
      const style = document.createElement('style')
      style.id = kfId
      style.textContent = `
        @keyframes ${animName} {
          0%   { transform:translateY(100vh) scale(0); opacity:0 }
          10%  { opacity:${peakOpacity} }
          90%  { opacity:${peakOpacity} }
          100% { transform:translateY(-10vh) scale(1); opacity:0 }
        }
      `
      document.head.appendChild(style)
    }

    return () => particles.forEach((p) => p.remove())
  }, [resolvedTheme])

  return (
    <section
      data-component="Hero"
      aria-label="Hero"
      className="relative min-h-[calc(100vh-140px)] lg:min-h-screen flex flex-col items-center justify-center text-center overflow-hidden px-1 sm:px-6 pt-24 pb-6 lg:pb-16"
    >
      <div data-part="background-gradients" className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div data-part="radial-gradients" className="absolute inset-0 bg-hero-ambience" />
        <div data-part="grid-overlay" className="absolute inset-0 hero-grid" />
      </div>

      <div
        data-part="particles"
        ref={particlesRef}
        className="absolute inset-0 overflow-hidden pointer-events-none"
        aria-hidden="true"
      />

      <div
        data-part="content"
        className="relative z-10 flex flex-col items-center w-full max-w-4xl mx-auto"
      >
        <Badge
          data-part="badge"
          color="carolina"
          size="pill"
          className="mb-6 sm:mb-8 backdrop-blur-md animate-fade-up"
        >
          THE TRACED METHOD&trade;
        </Badge>

        <h1
          data-part="headline"
          className="font-grotesk font-bold leading-none tracking-tight mb-4 sm:mb-5 animate-fade-up text-[clamp(36px,7vw,88px)] [animation-delay:150ms]"
        >
          THE ROAD TO
          <br />
          <span data-part="headline-gradient" className="text-grad-brand">
            AUTHENTICITY
          </span>
        </h1>

        <p
          data-part="subheadline"
          className="text-steel dark:text-cream/50 max-w-xl leading-relaxed font-light mb-10 sm:mb-12 animate-fade-up px-2 text-[clamp(16px,2.5vw,20px)] [animation-delay:300ms]"
        >
          Don&apos;t Detect. Prove.™ Explore verified VOD content, connect your smart wallet, and stream
          with confidence.
        </p>

        <div
          data-part="cta-buttons"
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center w-full sm:w-auto animate-fade-up [animation-delay:450ms]"
        >
          <LinkButton
            data-part="cta-primary"
            variant="filled"
            size="xl"
            href="/library"
            className="flex items-center justify-center gap-2.5"
          >
            VIEW WHAT&apos;S TRUE
          </LinkButton>
          <LinkButton
            data-part="cta-secondary"
            variant="ghost"
            size="xl"
            href="/#features"
            className="flex items-center justify-center"
          >
            HOW IT WORKS
          </LinkButton>
        </div>
      </div>

      <div
        data-part="scroll-indicator"
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 z-10 animate-fade-up [animation-delay:600ms]"
      >
        <span className="font-mono text-[16px] tracking-[3px] text-mist dark:text-cream/30">SCROLL</span>
        <div
          data-part="scroll-mouse"
          className="w-6 h-9 border-2 border-ink/20 dark:border-cream/20 rounded-xl relative"
        >
          <div
            data-part="scroll-dot"
            className="w-1 h-2 bg-carolina rounded-sm absolute left-1/2 -translate-x-1/2 animate-scroll-bounce top-1.5"
          />
        </div>
      </div>
    </section>
  )
}
